import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { Tooltip, Popover } from 'antd';
import { useTaskDrawer } from '../TaskDrawerContext';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENT_SIZE_MB, formatBytes, validateAttachmentFile } from '../../utils/attachmentUtils';
import AttachmentItem from './AttachmentItem';
import CommentItem from './CommentItem';
import Avatar from './Avatar';
import { fetchTaskAiSummary } from '../../api/projectManagementApi';
import { fetchFiles, classifyUpload } from '../../files/api/projectFilesApi';
import AiFileOrganizationModal from '../../files/components/AiFileOrganizationModal';
import PreUploadModal from './PreUploadModal';

export default function TaskTabs() {
    const {
        task,
        taskId,
        employees,
        currentAdminId,
        activeTab,
        setActiveTab,
        commentText,
        setCommentText,
        submittingComment,
        commentEndRef,
        handleAddComment,
        handleDeleteComment,
        handleDeleteAttachment,
        // Comment attachments & references
        commentAttachments,
        handleCommentFiles,
        removeCommentAttachment,
        commentFileReferences = [],
        setCommentFileReferences,
        // Attachment tab
        pendingAttachments,
        attachmentsDragOver,
        setAttachmentsDragOver,
        handleAttachmentTabFiles,
        handleAttachmentTabDrop,
        handleUploadPendingAttachments,
        removePendingAttachment,
        mode,
    } = useTaskDrawer();

    // @File Project Reference Picker state
    const [projectFiles, setProjectFiles] = useState([]);
    const [loadingProjectFiles, setLoadingProjectFiles] = useState(false);
    const [filePickerOpen, setFilePickerOpen] = useState(false);

    const targetProjectId = task?.project?._id || (typeof task?.project === 'string' ? task?.project : null);

    const loadProjectFiles = async () => {
        if (!targetProjectId && (!task?.attachments || task.attachments.length === 0)) return;
        setLoadingProjectFiles(true);
        try {
            let combined = [];
            if (targetProjectId) {
                const res = await fetchFiles({ projectId: targetProjectId, status: 'active' });
                if (res?.success && Array.isArray(res.data)) {
                    combined = [...res.data];
                }
            }
            // Merge task attachments so task deliverables & comment files are immediately referenceable
            if (Array.isArray(task?.attachments)) {
                task.attachments.forEach((att) => {
                    const attId = String(att._id || att.id);
                    if (!combined.some((f) => String(f.id || f._id) === attId)) {
                        combined.push({
                            id: attId,
                            _id: attId,
                            name: att.originalName || att.fileName || 'Attachment',
                            size: formatBytes(att.size || 0),
                            folderCode: 'Task Attachment',
                            publicUrl: att.publicUrl,
                            icon: 'attach_file',
                        });
                    }
                });
            }
            setProjectFiles(combined);
        } catch (e) {
            console.error('Error fetching project files for reference:', e);
        } finally {
            setLoadingProjectFiles(false);
        }
    };

    // Preload project files when task loads or when comments tab is active
    useEffect(() => {
        if (targetProjectId || (task?.attachments && task.attachments.length > 0)) {
            loadProjectFiles();
        }
    }, [targetProjectId, task?._id, task?.attachments?.length]);

    // AI File Organization State (Section 21)
    const [aiOrgModalOpen, setAiOrgModalOpen] = useState(false);
    const [aiOrgFile, setAiOrgFile] = useState(null);
    const [aiOrgData, setAiOrgData] = useState(null);
    const [aiOrgTargetContext, setAiOrgTargetContext] = useState('comment');

    // Pre-Upload Modal state — collects one-word description + reviewer tags before AI classification
    const [preUploadOpen, setPreUploadOpen] = useState(false);
    const [preUploadFile, setPreUploadFile] = useState(null);
    const [preUploadContext, setPreUploadContext] = useState('comment');
    const [uploadSizeError, setUploadSizeError] = useState('');

    /**
     * Entry point for every file selection (click or drag-drop).
     * Step 1: Validate size & type.
     * Step 2: Open PreUploadModal to collect description + reviewer tags.
     * Step 3: After confirmation, run AI classifyUpload → AI Org Modal.
     */
    const handleInitiateUpload = (fileList, context = 'comment') => {
        if (!fileList || fileList.length === 0) return;
        const rawFiles = Array.from(fileList);
        const primaryFile = rawFiles[0];

        // ── Client-side size & type validation (100 MB = 104,857,600 bytes) ──
        const validation = validateAttachmentFile(primaryFile);
        if (!validation.valid) {
            setUploadSizeError(validation.reason);
            // Auto-clear the error after 5 s
            setTimeout(() => setUploadSizeError(''), 5000);
            return;
        }
        setUploadSizeError('');

        if (!targetProjectId) {
            // No project context — skip AI flow, attach directly
            if (context === 'comment') handleCommentFiles(rawFiles);
            else handleAttachmentTabFiles(rawFiles);
            return;
        }

        // Open pre-upload description + reviewer modal
        setPreUploadFile(primaryFile);
        setPreUploadContext(context);
        setPreUploadOpen(true);
    };

    /**
     * Called when user confirms the PreUploadModal (description + reviewers set).
     * Runs AI classify-upload then opens AI File Organization Modal.
     */
    const handlePreUploadConfirm = async ({ description, taggedReviewers }) => {
        setPreUploadOpen(false);
        const file = preUploadFile;
        const context = preUploadContext;
        if (!file) return;

        try {
            const res = await classifyUpload({
                projectId: targetProjectId,
                taskId: task?._id,
                fileName: file.name,
                mimeType: file.type,
                // Inject the description label into commentText so classifyUpload uses it for context
                commentText: `${description} ${commentText || ''}`.trim(),
            });

            if (res?.success && res.data) {
                // Attach metadata for downstream handlers
                file._preUploadMeta = { description, taggedReviewers };
                setAiOrgFile(file);
                setAiOrgData(res.data);
                setAiOrgTargetContext(context);
                setAiOrgModalOpen(true);
            } else {
                if (context === 'comment') handleCommentFiles([file]);
                else handleAttachmentTabFiles([file]);
            }
        } catch (e) {
            console.error('Error in AI file classification:', e);
            if (context === 'comment') handleCommentFiles([file]);
            else handleAttachmentTabFiles([file]);
        }
    };

    const handleAcceptAiOrganization = (configuredData) => {
        if (!aiOrgFile) return;
        // Carry pre-upload metadata (description + reviewer tags) into the AI config
        const meta = aiOrgFile._preUploadMeta || {};
        aiOrgFile.aiConfig = { ...configuredData, ...meta };
        if (configuredData.customFileName) {
            try {
                Object.defineProperty(aiOrgFile, 'name', {
                    writable: true,
                    value: configuredData.customFileName,
                });
            } catch {
                // Ignore if read-only in some browser engines
            }
        }

        if (aiOrgTargetContext === 'comment') {
            handleCommentFiles([aiOrgFile]);
        } else {
            handleAttachmentTabFiles([aiOrgFile]);
        }
        setAiOrgModalOpen(false);
        setAiOrgFile(null);
        setAiOrgData(null);
    };

    const handleChooseExistingFileFromAi = () => {
        setAiOrgModalOpen(false);
        setAiOrgFile(null);
        setAiOrgData(null);
        loadProjectFiles();
        setFilePickerOpen(true);
    };

    // AI Summary inline tab state
    const [aiSummaryHtml, setAiSummaryHtml] = useState('');
    const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
    const [aiSummaryError, setAiSummaryError] = useState('');
    const [aiSummaryFetchedId, setAiSummaryFetchedId] = useState(null);

    const handleLoadAiSummary = useCallback((force = false) => {
        if (!task?._id) return;
        setAiSummaryLoading(true);
        if (force) setAiSummaryHtml('');
        setAiSummaryError('');

        fetchTaskAiSummary(task._id, force)
            .then((res) => {
                setAiSummaryHtml(res.data?.result || '');
                setAiSummaryFetchedId(task._id);
            })
            .catch((err) => {
                setAiSummaryError(err?.response?.data?.message || 'Failed to generate summary. Please try again.');
            })
            .finally(() => setAiSummaryLoading(false));
    }, [task?._id]);

    // Fetch AI summary when the tab becomes active
    useEffect(() => {
        if (activeTab !== 'ai-summary' || !task?._id) return;
        if (aiSummaryFetchedId === task._id) return; // already fetched
        handleLoadAiSummary(false);
    }, [activeTab, task?._id, aiSummaryFetchedId, handleLoadAiSummary]);

    // Reset cache when task changes
    useEffect(() => {
        if (task?._id && task._id !== aiSummaryFetchedId) {
            setAiSummaryHtml('');
            setAiSummaryFetchedId(null);
        }
    }, [task?._id]);

    // Separate file input refs so they don't conflict
    const attachTabInputRef = useRef(null);
    const commentInputRef = useRef(null);

    const hasPendingToUpload = pendingAttachments.some((f) => f.status === 'pending' || f.status === 'error');

    // Mention & Autocomplete state
    const [mentionState, setMentionState] = useState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const textareaRef = useRef(null);
    const mentionListRef = useRef(null); // for auto-scroll on arrow key navigation

    // Auto-scroll the highlighted mention item into view when navigating with arrow keys
    useEffect(() => {
        if (!mentionState.active || !mentionListRef.current) return;
        const el = mentionListRef.current.querySelector('[data-mention-selected="true"]');
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [selectedMentionIndex, mentionState.active]);

    const filteredPeople = (employees || []).filter((emp) => {
        if (!mentionState.query) return true;
        const q = mentionState.query.toLowerCase();
        const name = (emp.name || '').toLowerCase();
        const surname = (emp.surname || '').toLowerCase();
        const email = (emp.email || '').toLowerCase();
        return name.includes(q) || surname.includes(q) || `${name} ${surname}`.includes(q) || email.includes(q);
    }).slice(0, 20);

    const filteredProjectFiles = projectFiles.filter((file) => {
        if (!mentionState.query) return true;
        const q = mentionState.query.toLowerCase();
        return (
            (file.name || '').toLowerCase().includes(q) ||
            (file.folderCode || '').toLowerCase().includes(q) ||
            (file.revisionRound || '').toLowerCase().includes(q)
        );
    }).slice(0, 15);

    const totalMentionItems = [
        ...filteredPeople.map((p) => ({ type: 'person', data: p })),
        ...filteredProjectFiles.map((f) => ({ type: 'file', data: f })),
    ];

    const handleSelectUserMention = (user) => {
        const username = user.name || user.email?.split('@')[0] || 'user';
        if (mentionState.active) {
            const before = commentText.slice(0, mentionState.matchStart);
            const after = commentText.slice(mentionState.cursorPosition);
            const newText = `${before}@${username} ${after}`;
            setCommentText(newText);
            setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
        } else {
            setCommentText((prev) => (prev ? `${prev} @${username} ` : `@${username} `));
        }
        setTimeout(() => textareaRef.current?.focus(), 50);
    };

    const handleSelectFileReference = (file) => {
        const fileId = String(file.id || file._id);
        if (!commentFileReferences.some((f) => String(f.id || f._id) === fileId)) {
            setCommentFileReferences((prev) => [...prev, file]);
        }
        if (mentionState.active) {
            const before = commentText.slice(0, mentionState.matchStart);
            const after = commentText.slice(mentionState.cursorPosition);
            const newText = `${before}@File[${file.name}] ${after}`;
            setCommentText(newText);
            setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
        } else {
            setCommentText((prev) => (prev ? `${prev} @File[${file.name}] ` : `@File[${file.name}] `));
        }
        setFilePickerOpen(false);
        setTimeout(() => textareaRef.current?.focus(), 50);
    };

    const handleTextareaChange = (e) => {
        const val = e.target.value;
        const sel = e.target.selectionStart;
        setCommentText(val);

        const textBeforeCursor = val.slice(0, sel);
        const match = textBeforeCursor.match(/(?:^|\s)@([^\s]*)$/);
        if (match) {
            const query = match[1];
            const matchStart = sel - query.length - 1;
            setMentionState({ active: true, query, matchStart, cursorPosition: sel });
            setSelectedMentionIndex(0);
            if (projectFiles.length === 0) {
                loadProjectFiles();
            }
        } else {
            if (mentionState.active) {
                setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
            }
        }
    };

    const handleTextareaKeyDown = (e) => {
        if (mentionState.active && totalMentionItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex((prev) => (prev + 1) % totalMentionItems.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex((prev) => (prev - 1 + totalMentionItems.length) % totalMentionItems.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const target = totalMentionItems[selectedMentionIndex] || totalMentionItems[0];
                if (target) {
                    if (target.type === 'person') handleSelectUserMention(target.data);
                    else if (target.type === 'file') handleSelectFileReference(target.data);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddComment();
        }
    };

    return (
        <>
            <section className="space-y-4">
                {mode === 'create' ? (
                    <>
                        <div className="flex items-center border-b border-surface-container-high gap-1 opacity-50 pointer-events-none">
                            <TabButton id="comments" activeTab="comments">Comments (0)</TabButton>
                            <TabButton id="attachments" activeTab="">Attachments (0)</TabButton>
                            <button className="px-4 py-2 text-sm font-bold flex items-center gap-1.5 text-on-surface-variant">
                                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                AI Summary
                            </button>
                        </div>
                        <div className="flex flex-col items-center justify-center py-8 text-center bg-surface-container-lowest border border-dashed border-surface-container-high rounded-xl">
                            <span className="material-symbols-outlined text-3xl text-on-surface-variant opacity-50 mb-2">lock</span>
                            <p className="text-sm font-semibold text-on-surface-variant">Save task to unlock tabs</p>
                            <p className="text-xs text-on-surface-variant opacity-70 mt-1">Comments and attachments will be available after creation.</p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Tab bar */}
                        <div className="flex items-center border-b border-surface-container-high gap-1">
                            <TabButton id="comments" activeTab={activeTab} setActiveTab={setActiveTab}>
                                Comments ({task.comments?.length || 0})
                            </TabButton>
                            <TabButton id="attachments" activeTab={activeTab} setActiveTab={setActiveTab}>
                                Attachments ({task.attachments?.length || 0})
                            </TabButton>
                            <button
                                onClick={() => setActiveTab('ai-summary')}
                                className={`px-4 py-2 text-sm font-bold flex items-center gap-1.5 transition-all rounded-t-lg ${activeTab === 'ai-summary'
                                    ? 'bg-primary-container/10 text-primary border-b-2 border-primary'
                                    : 'text-on-surface-variant hover:bg-primary-container/5 hover:text-primary'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                AI Summary
                            </button>
                        </div>

                        {/* ── Comments Tab ────────────────────────────────────────── */}
                        {activeTab === 'comments' && (
                            <div className="space-y-4">
                                {/* Comment list */}
                                <div className="space-y-5 max-h-[360px] overflow-y-auto pr-1">
                                    {task.comments?.map((comment) => (
                                        <CommentItem
                                            key={comment._id}
                                            comment={comment}
                                            currentAdminId={currentAdminId}
                                            onDelete={handleDeleteComment}
                                            taskId={taskId}
                                        />
                                    ))}
                                    <div ref={commentEndRef} />
                                    {(!task.comments || task.comments.length === 0) && (
                                        <p className="text-center text-xs text-ed-on-surface-variant opacity-60 py-4">
                                            No comments yet. Start the conversation!
                                        </p>
                                    )}
                                </div>

                                {/* Comment input + attachment strip */}
                                <div className="space-y-2 border-t border-ed-surface-container-high pt-3">
                                    {/* Pending comment attachments & @File references */}
                                    {(commentAttachments.length > 0 || (commentFileReferences && commentFileReferences.length > 0)) && (
                                        <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                                            {commentAttachments.map((entry) => (
                                                <PendingFileChip
                                                    key={entry.id}
                                                    entry={entry}
                                                    onRemove={removeCommentAttachment}
                                                />
                                            ))}
                                            {(commentFileReferences || []).map((ref) => (
                                                <span
                                                    key={ref.id || ref._id}
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#eff4ff] dark:bg-[#1a2538] text-[#0070F3] border border-[#0070F3]/30 rounded-lg text-xs font-semibold shadow-2xs"
                                                >
                                                    <span className="material-symbols-outlined text-sm">folder_shared</span>
                                                    <span className="truncate max-w-[160px]">{ref.name || ref.originalName}</span>
                                                    <span className="text-[10px] opacity-70">(@File)</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCommentFileReferences((prev) => prev.filter((f) => (f.id || f._id) !== (ref.id || ref._id)))}
                                                        className="hover:text-red-500 text-slate-400 ml-0.5 transition-colors"
                                                    >
                                                        ✕
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Text input row with avatar on left and inline actions */}
                                    <div className="flex gap-3 items-start">
                                        <Avatar person={(employees || []).find(e => e._id === currentAdminId)} size={32} />
                                        <div className="flex-1 relative">
                                            {/* Inline @ Mention Popup */}
                                            {mentionState.active && (
                                                <div className="absolute bottom-full left-0 mb-2 w-80 max-h-80 bg-white dark:bg-[#1e1e1e] rounded-xl shadow-xl border border-slate-200 dark:border-[#333] z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-150">
                                                    <div className="px-3 py-1.5 bg-slate-50 dark:bg-[#252525] border-b border-slate-100 dark:border-[#333] flex items-center justify-between text-[11px] font-bold text-slate-500">
                                                        <span className="flex items-center gap-1.5 text-[#03488A] dark:text-[#00AAF1]">
                                                            <span className="material-symbols-outlined text-[15px]">alternate_email</span>
                                                            MENTION USER OR FILE
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 font-normal">Use ↑↓ & Enter</span>
                                                    </div>
                                                    <div ref={mentionListRef} className="overflow-y-auto max-h-[260px] p-1 space-y-1">
                                                        {/* Section 1: People */}
                                                        {filteredPeople.length > 0 && (
                                                            <div>
                                                                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                                    People
                                                                </div>
                                                                {filteredPeople.map((person, idx) => (
                                                                    <div
                                                                        key={person._id || idx}
                                                                        data-mention-selected={idx === selectedMentionIndex ? 'true' : 'false'}
                                                                        onMouseEnter={() => setSelectedMentionIndex(idx)}
                                                                        onClick={() => handleSelectUserMention(person)}
                                                                        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${
                                                                            idx === selectedMentionIndex
                                                                                ? 'bg-[#eff4ff] text-[#03488a] dark:bg-[#1a2538] dark:text-white'
                                                                                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                        }`}
                                                                    >
                                                                        <Avatar person={person} size={22} />
                                                                        <div className="truncate flex-1 min-w-0">
                                                                            <p className="font-semibold truncate text-[12px]">
                                                                                {person.name} {person.surname}
                                                                            </p>
                                                                            <p className="text-[10px] text-slate-400 truncate">
                                                                                @{person.name || person.email?.split('@')[0]} {person.department ? `· ${person.department}` : ''}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Section 2: Project Files */}
                                                        {filteredProjectFiles.length > 0 && (
                                                            <div>
                                                                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-t border-slate-100 dark:border-[#333] mt-1 pt-1">
                                                                    Project Files (@File)
                                                                </div>
                                                                {filteredProjectFiles.map((file, idx) => {
                                                                    const actualIdx = filteredPeople.length + idx;
                                                                    return (
                                                                        <div
                                                                            key={file.id || file._id}
                                                                            data-mention-selected={actualIdx === selectedMentionIndex ? 'true' : 'false'}
                                                                            onMouseEnter={() => setSelectedMentionIndex(actualIdx)}
                                                                            onClick={() => handleSelectFileReference(file)}
                                                                            className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${
                                                                                actualIdx === selectedMentionIndex
                                                                                    ? 'bg-[#eff4ff] text-[#0070F3] dark:bg-[#1a2538] dark:text-white'
                                                                                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                            }`}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[17px] text-[#0070F3]">
                                                                                {file.icon || 'description'}
                                                                            </span>
                                                                            <div className="truncate flex-1 min-w-0">
                                                                                <p className="font-semibold truncate text-[12px]">{file.name}</p>
                                                                                <p className="text-[10px] text-slate-400 truncate">
                                                                                    {file.folderCode ? `Folder ${file.folderCode}` : ''} {file.size ? `· ${file.size}` : ''}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {totalMentionItems.length === 0 && (
                                                            <div className="py-3 text-center text-xs text-slate-400">
                                                                No matching people or project files
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            <textarea
                                                ref={textareaRef}
                                                value={commentText}
                                                onChange={handleTextareaChange}
                                                placeholder="Add a comment or type @ to mention people or reference project files…"
                                                onKeyDown={handleTextareaKeyDown}
                                                disabled={submittingComment}
                                                rows={3}
                                                className="w-full bg-ed-surface-container-lowest dark:bg-[#141414] border border-ed-surface-container-high dark:border-[#303030] rounded-xl text-sm p-3 pr-24 dark:text-white focus:ring-1 focus:ring-primary focus:outline-none resize-none transition-all min-h-[80px]"
                                            />
                                            <div className="absolute right-3 bottom-3 flex items-center gap-2">
                                                {/* Reference project file (@File) button with Popover */}
                                                <Popover
                                                    open={filePickerOpen}
                                                    onOpenChange={(open) => {
                                                        setFilePickerOpen(open);
                                                        if (open) loadProjectFiles();
                                                    }}
                                                    trigger="click"
                                                    placement="topLeft"
                                                    content={
                                                        <div className="w-72 max-h-64 overflow-y-auto p-1 space-y-1">
                                                            <div className="px-2 py-1 text-[11px] font-bold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider border-b border-slate-100 dark:border-[#303030]">
                                                                Reference Project File (@File)
                                                            </div>
                                                            {loadingProjectFiles ? (
                                                                <div className="py-4 text-center text-xs text-slate-400">Loading project files...</div>
                                                            ) : projectFiles.length === 0 ? (
                                                                <div className="py-4 text-center text-xs text-slate-400">No project files found</div>
                                                            ) : (
                                                                projectFiles.map((file) => (
                                                                    <div
                                                                        key={file.id}
                                                                        onClick={() => handleSelectFileReference(file)}
                                                                        className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-xs transition-colors"
                                                                    >
                                                                        <span className="material-symbols-outlined text-sm text-[#0070F3]">
                                                                            {file.icon || 'description'}
                                                                        </span>
                                                                        <div className="truncate flex-1">
                                                                            <p className="font-semibold truncate text-[#0b1c30] dark:text-[#eaf1ff]">{file.name}</p>
                                                                            <p className="text-[10px] text-slate-400 truncate">
                                                                                {file.revisionRound || file.subfolderCategory || file.folderCode || 'File'} • {file.size}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    }
                                                >
                                                    <Tooltip title="Reference Project File (@File)">
                                                        <button
                                                            type="button"
                                                            disabled={submittingComment || !targetProjectId}
                                                            className="text-ed-on-surface-variant hover:text-[#0070F3] transition-colors disabled:opacity-40"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">folder_shared</span>
                                                        </button>
                                                    </Tooltip>
                                                </Popover>

                                                {/* Attach file button */}
                                                <Tooltip title="Attach file">
                                                    <button
                                                        type="button"
                                                        onClick={() => commentInputRef.current?.click()}
                                                        disabled={submittingComment}
                                                        className="text-ed-on-surface-variant hover:text-primary transition-colors disabled:opacity-40"
                                                    >
                                                        <span className="material-symbols-outlined text-lg">attach_file</span>
                                                    </button>
                                                </Tooltip>

                                                {/* Send button */}
                                                <button
                                                    type="button"
                                                    onClick={handleAddComment}
                                                    disabled={submittingComment || (!commentText.trim() && commentAttachments.length === 0 && (commentFileReferences?.length || 0) === 0)}
                                                    className="text-primary hover:opacity-85 transition-opacity disabled:opacity-40"
                                                >
                                                    {submittingComment ? (
                                                        <LoadingOutlined style={{ fontSize: 18 }} />
                                                    ) : (
                                                        <span className="material-symbols-outlined text-lg">send</span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-xs text-ed-on-surface-variant opacity-50 pl-11">
                                        Press Enter to send · Shift+Enter for new line · Type @ or click 📁 to reference project files · Attach files with 📎
                                    </p>
                                    {/* Upload size / type error (comment context) */}
                                    {uploadSizeError && (
                                        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl text-xs text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 duration-200 ml-11">
                                            <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5">error</span>
                                            <span>{uploadSizeError}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Hidden file input for comment attachments */}
                                <input
                                    ref={commentInputRef}
                                    type="file"
                                    multiple
                                    accept={ATTACHMENT_ACCEPT}
                                    className="hidden"
                                    onChange={(e) => { handleInitiateUpload(e.target.files, 'comment'); e.target.value = ''; }}
                                />
                            </div>
                        )}

                        {/* â”€â”€ Attachments Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
                        {activeTab === 'attachments' && (
                            <div className="space-y-4">
                                {/* Existing saved attachments */}
                                {task.attachments?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto pr-1">
                                        {task.attachments.map((attachment) => (
                                            <AttachmentItem
                                                key={attachment._id}
                                                attachment={attachment}
                                                taskId={taskId}
                                                onDelete={handleDeleteAttachment}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    pendingAttachments.length === 0 && (
                                        <div className="text-center py-4">
                                            <p className="text-xs text-on-surface-variant opacity-60">No files attached yet.</p>
                                        </div>
                                    )
                                )}

                                {/* Pending uploads list */}
                                {pendingAttachments.length > 0 && (
                                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                        {pendingAttachments.map((entry) => (
                                            <PendingFileChip
                                                key={entry.id}
                                                entry={entry}
                                                onRemove={removePendingAttachment}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Drop zone */}
                                <div
                                    onClick={() => attachTabInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setAttachmentsDragOver(true); }}
                                    onDragLeave={() => setAttachmentsDragOver(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setAttachmentsDragOver(false);
                                        handleInitiateUpload(e.dataTransfer.files, 'attachment');
                                    }}
                                    className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all
                                ${attachmentsDragOver
                                            ? 'border-primary bg-primary/5'
                                            : 'border-surface-container-high hover:border-primary/40 hover:bg-surface-container-low'
                                        }`}
                                >
                                    <CloudUploadOutlined
                                        style={{ fontSize: 28, color: attachmentsDragOver ? '#03488A' : '#737782' }}
                                    />
                                    <div className="text-center">
                                        <p className="text-sm font-semibold text-on-surface">
                                            Click to upload or drag & drop
                                        </p>
                                        <p className="text-xs text-on-surface-variant mt-0.5">
                                            Documents, images, archives, media · Max {MAX_ATTACHMENT_SIZE_MB}MB each
                                        </p>
                                    </div>
                                </div>

                                {/* Upload size / type error banner */}
                                {uploadSizeError && (
                                    <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl text-xs text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 duration-200">
                                        <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5">error</span>
                                        <span>{uploadSizeError}</span>
                                    </div>
                                )}

                                {/* Hidden input */}
                                <input
                                    ref={attachTabInputRef}
                                    type="file"
                                    multiple
                                    accept={ATTACHMENT_ACCEPT}
                                    className="hidden"
                                    onChange={(e) => { handleInitiateUpload(e.target.files, 'attachment'); e.target.value = ''; }}
                                />

                                {/* Upload button — only shows when there are files queued */}
                                {pendingAttachments.length > 0 && (
                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleUploadPendingAttachments}
                                            disabled={!hasPendingToUpload}
                                            className="flex items-center gap-2 px-5 py-2 brand-gradient text-on-primary text-xs font-bold rounded-xl shadow-md hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
                                        >
                                            <CloudUploadOutlined />
                                            Upload {pendingAttachments.filter((f) => f.status === 'pending' || f.status === 'error').length} file(s)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'ai-summary' && (
                            <div>
                                {aiSummaryLoading && (
                                    <div className="space-y-5 animate-pulse pt-2">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-4 h-4 rounded-full bg-primary/30" />
                                            <div className="h-3 w-36 rounded bg-surface-container-high" />
                                        </div>
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="space-y-2">
                                                <div className="h-2.5 w-24 rounded bg-primary/20" />
                                                <div className="h-2.5 rounded bg-surface-container-high w-full" />
                                                <div className="h-2.5 rounded w-4/5 bg-surface-container-high opacity-70" />
                                            </div>
                                        ))}
                                        <p className="text-xs text-on-surface-variant text-center pt-2 flex items-center justify-center gap-2">
                                            <span className="material-symbols-outlined text-primary animate-spin" style={{ fontSize: '15px' }}>progress_activity</span>
                                            Analysing task data with AI…
                                        </p>
                                    </div>
                                )}
                                {!aiSummaryLoading && aiSummaryError && (
                                    <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
                                        <div className="w-12 h-12 rounded-full bg-error-container flex items-center justify-center">
                                            <span className="material-symbols-outlined text-error" style={{ fontSize: '24px' }}>error</span>
                                        </div>
                                        <p className="text-sm font-semibold text-on-surface">Something went wrong</p>
                                        <p className="text-xs text-on-surface-variant max-w-sm">{aiSummaryError}</p>
                                        <button
                                            onClick={() => { setAiSummaryFetchedId(null); setAiSummaryHtml(''); setAiSummaryError(''); }}
                                            className="px-4 py-1.5 rounded-lg text-xs font-bold text-white brand-gradient"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                )}
                                {!aiSummaryLoading && aiSummaryHtml && (
                                    <div>
                                        <div
                                            className="ai-summary-content"
                                            dangerouslySetInnerHTML={{ __html: aiSummaryHtml }}
                                        />
                                        <div className="mt-4 pt-3 border-t border-surface-container-high flex items-center justify-between">
                                            <p className="text-[11px] text-on-surface-variant opacity-50 flex items-center gap-1.5">
                                                <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#03488a' }}>bolt</span>
                                                Powered by GPT-4o mini · Based on task data, comments & subtasks
                                            </p>
                                            <button
                                                onClick={() => handleLoadAiSummary(true)}
                                                className="flex items-center gap-1 text-[11px] font-semibold text-on-surface-variant hover:text-primary transition-colors cursor-pointer"
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>refresh</span>
                                                Regenerate
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {aiSummaryLoading && (
                                    <p className="text-[11px] text-on-surface-variant opacity-40 flex items-center gap-1.5 pt-3">
                                        <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#03488a' }}>bolt</span>
                                        Powered by GPT-4o mini · Based on task data, comments &amp; subtasks
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* AI File Organization Modal (Section 21 Spec) */}
            <AiFileOrganizationModal
                open={aiOrgModalOpen}
                file={aiOrgFile}
                aiData={aiOrgData}
                onAccept={handleAcceptAiOrganization}
                onChooseExistingFile={handleChooseExistingFileFromAi}
                onCancel={() => {
                    setAiOrgModalOpen(false);
                    setAiOrgFile(null);
                    setAiOrgData(null);
                }}
            />

            {/* Pre-Upload Description & Reviewer Tagging Modal */}
            <PreUploadModal
                open={preUploadOpen}
                file={preUploadFile}
                employees={employees || []}
                onConfirm={handlePreUploadConfirm}
                onCancel={() => {
                    setPreUploadOpen(false);
                    setPreUploadFile(null);
                }}
            />
        </>
    );
}

/** Pick icon name + background colour for file type */
function getFileIconInfo(name, mime = '') {
    const filename = (name || '').toLowerCase();
    mime = mime.toLowerCase();
    if (mime.includes('pdf') || filename.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bg: '#fde8e8', color: '#c0392b' };
    if (mime.startsWith('image/') || filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif') || filename.endsWith('.svg') || filename.endsWith('.webp'))
        return { icon: 'image', bg: '#e8f4fd', color: '#2980b9' };
    if (mime.includes('word') || filename.endsWith('.doc') || filename.endsWith('.docx') || filename.endsWith('.odt'))
        return { icon: 'description', bg: '#e8edf8', color: '#2b4da0' };
    if (mime.includes('sheet') || filename.endsWith('.xls') || filename.endsWith('.xlsx') || filename.endsWith('.csv'))
        return { icon: 'table_chart', bg: '#e6f4ee', color: '#1a7a45' };
    if (mime.includes('zip') || mime.includes('rar') || filename.endsWith('.zip') || filename.endsWith('.rar') || filename.endsWith('.7z') || filename.endsWith('.tar') || filename.endsWith('.gz'))
        return { icon: 'folder_zip', bg: '#fef9e6', color: '#b7950b' };
    if (mime.includes('video/') || filename.endsWith('.mp4') || filename.endsWith('.mov') || filename.endsWith('.avi') || filename.endsWith('.mkv') || filename.endsWith('.webm'))
        return { icon: 'videocam', bg: '#f0e6fe', color: '#7d3c98' };
    if (mime.includes('audio/') || filename.endsWith('.mp3') || filename.endsWith('.wav') || filename.endsWith('.m4a') || filename.endsWith('.ogg') || filename.endsWith('.flac'))
        return { icon: 'audio_file', bg: '#fde8f5', color: '#c0407a' };
    return { icon: 'attach_file', bg: '#efefef', color: '#666' };
}

/** Compact chip showing a pending / uploading / done / error file */
function PendingFileChip({ entry, onRemove }) {
    const { icon, bg, color } = getFileIconInfo(entry.name, entry.type);

    return (
        <div className="flex items-center gap-2 bg-surface-container-low border border-surface-container-high rounded-xl px-2 py-1.5 min-w-[200px] max-w-[300px] flex-1">
            {entry.previewUrl ? (
                <img src={entry.previewUrl} alt={entry.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
            ) : (
                <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                    <span className="material-symbols-outlined text-[16px]" style={{ color }}>{icon}</span>
                </div>
            )}

            <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-on-surface truncate">{entry.name}</p>
                <p className="text-[11px] text-on-surface-variant">
                    {formatBytes(entry.size)}
                    {entry.status === 'uploading' && ` Â· ${entry.progress || 0}%`}
                    {entry.error && <span className="text-error"> Â· {entry.error}</span>}
                </p>
                {entry.status === 'uploading' && (
                    <div className="h-1 mt-1 bg-surface-container-high rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#03488A] to-[#006591] transition-all"
                            style={{ width: `${entry.progress || 0}%` }}
                        />
                    </div>
                )}
            </div>

            <div className="flex-shrink-0">
                {entry.status === 'uploading' && <LoadingOutlined style={{ fontSize: 13 }} className="text-primary" />}
                {entry.status === 'done' && <CheckCircleOutlined style={{ fontSize: 13 }} className="text-success" />}
                {(entry.status === 'pending' || entry.status === 'error') && (
                    <button
                        onClick={() => onRemove(entry.id)}
                        className="p-0.5 hover:text-error text-on-surface-variant transition-colors"
                    >
                        <DeleteOutlined style={{ fontSize: 13 }} />
                    </button>
                )}
            </div>
        </div>
    );
}

function TabButton({ id, activeTab, setActiveTab, children }) {
    return (
        <button
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-all ${activeTab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-primary'
                }`}
        >
            {children}
        </button>
    );
}
